import React, { useEffect, useState, useRef } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Snackbar, Alert, Typography, Select, MenuItem, TablePagination
} from "@mui/material";
import { Add, Edit, Delete, Undo } from "@mui/icons-material";
import { axiosInstances } from "./apiConfig";

export default function CustomerUI() {
  // const API_BASE = "/api/customers";

  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", addresses: [] });
  const [editing, setEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [file, setFile] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const deleteTimers = useRef({});
  const [pendingDeletes, setPendingDeletes] = useState({});


  const loadCustomers = async (pageNum = page, size = rowsPerPage) => {
    try {
      const res = await axiosInstances.customerApi.get(`/?page=${pageNum}&size=${size}`);
      const result = res.data?.data || {};
      setCustomers(result.data || []);
      setTotal(result.total || result.data?.length || 0);
    } catch (err) {
      console.error("Error fetching customers", err);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [page, rowsPerPage]);

  const handleOpen = (customer = {}) => {
    setForm({
      ...customer,
      addresses: customer.addresses || [{ addressType: "", address_1: "", address_2: "", address_3: "", streetNo: "", landMark: "", locality: "", city: "", state: "", pincode: "" }]
    });
    setEditing(!!customer.id);
    setOpen(true);
  };

  const handleClose = () => {
    setForm({ name: "", email: "", phone: "", addresses: [] });
    setFile(null);
    setOpen(false);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAddressChange = (index, e) => {
    const newAddresses = [...form.addresses];
    newAddresses[index][e.target.name] = e.target.value;
    setForm({ ...form, addresses: newAddresses });
  };

  const addAddress = () => {
    setForm({ ...form, addresses: [...form.addresses, { addressType: "", address_1: "", address_2: "", address_3: "", streetNo: "", landMark: "", locality: "", city: "", state: "", pincode: "" }] });
  };

  const removeAddress = (index) => {
    const newAddresses = [...form.addresses];
    newAddresses.splice(index, 1);
    setForm({ ...form, addresses: newAddresses });
  };

  const handleSave = async () => {
  try {
    if (file) {
      // Upload Excel file for customers
      const excelFormData = new FormData();
      excelFormData.append("file", file);

      await axiosInstances.customerApi.post(`/uploadExcel`, excelFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSnackbar({ open: true, message: "File uploaded successfully!", severity: "success" });
    } else {
      const customerPayload = { ...form }; // Assuming 'form' matches your Customer structure

      if (editing) {
        // Update customer
        await axiosInstances.customerApi.post(`/update`, customerPayload, {
          headers: { "Content-Type": "application/json" },
        });
        setSnackbar({ open: true, message: "Customer updated successfully!", severity: "success" });
      } else {
        // Create customer
        await axiosInstances.customerApi.post(`/save`, customerPayload, {
          headers: { "Content-Type": "application/json" },
        });
        setSnackbar({ open: true, message: "Customer created successfully!", severity: "success" });
      }
    }

    loadCustomers();  // Reload customers list
    handleClose();    // Close the dialog
    setFile(null);    // Clear selected file
  } catch (err) {
    if (err.response && err.response.status === 409) {
      setSnackbar({ open: true, message: "Email already exists", severity: "error" });
    } else {
      setSnackbar({ open: true, message: "Something went wrong!", severity: "error" });
    }
  }
};

const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };


  const handleDelete = (id) => {
    setPendingDeletes((prev) => ({ ...prev, [id]: true }));

    deleteTimers.current[id] = setTimeout(async () => {
      try {
        const payload = { id }; // matches your CustomerReq structure (adjust if more fields are needed)
        await axiosInstances.customerApi.post(`/delete`, payload);
        setSnackbar({ open: true, message: "Customer deleted successfully!", severity: "success" });
        loadCustomers();
      } catch (err) {
        console.error("Delete failed", err);
        setSnackbar({ open: true, message: "Something went wrong!", severity: "error" });
      } finally {
        setPendingDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        deleteTimers.current[id] = null;
      }
    }, 30000); // 30 seconds
  };


  const handleUndo = (id) => {
    if (deleteTimers.current[id]) {
      clearTimeout(deleteTimers.current[id]);
      deleteTimers.current[id] = null;
    }

    setPendingDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Customer Management</Typography>
      <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
        Add Customer
      </Button>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Addresses Count</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((cust, index) => (
              <TableRow key={cust.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{cust.id}</TableCell>
                <TableCell>{cust.name}</TableCell>
                <TableCell>{cust.email}</TableCell>
                <TableCell>{cust.phone}</TableCell>
                <TableCell>{cust.addresses?.length || 0}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(cust)}><Edit /></IconButton>
                  {/* <IconButton color="error" onClick={() => handleDelete(cust.id)}><Delete /></IconButton> */}
                  {pendingDeletes[cust.id] ? (
                    <IconButton color="warning" onClick={() => handleUndo(cust.id)}><Undo /></IconButton>
                  ) : (
                    <IconButton color="error" onClick={() => handleDelete(cust.id)}><Delete /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {customers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">No customers found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelDisplayedRows={({ page }) =>
            `Page ${page + 1} of ${Math.ceil(total / rowsPerPage)}`
          }
        />
      </TableContainer>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Edit Customer" : "Add Customer"}</DialogTitle>
        <DialogContent dividers>
          {editing && (
            <TextField
              fullWidth
              label="Customer ID"
              name="id"
              value={form.id || ""}
              margin="dense"
              InputProps={{ readOnly: true }}
              disabled
            />
          )}
          <TextField fullWidth label="Name" name="name" value={form.name || ""} onChange={handleChange} margin="dense" />
          <TextField fullWidth label="Email" name="email" value={form.email || ""} onChange={handleChange} margin="dense" />
          <TextField fullWidth label="Phone" name="phone" value={form.phone || ""} onChange={handleChange} margin="dense" />

          {!editing && (
                      <>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          sx={{ mt: 2 }}
                        >
                          Upload File
                          <input
                            type="file"
                            hidden
                            onChange={handleFileChange}
                          />
                        </Button>
                        {file && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Selected: {file.name}
                          </Typography>
                        )}
                      </>
                    )}

          <Typography variant="h6" mt={2}>Addresses</Typography>
          {form.addresses.map((addr, idx) => (
            <Box key={idx} border={1} borderRadius={2} p={2} mb={2}>
              {addr.id && (
                <TextField
                  fullWidth
                  label="Address ID"
                  name="id"
                  value={addr.id}
                  margin="dense"
                  InputProps={{ readOnly: true }}
                  sx={{ mb: 1 }}
                  disabled
                />
              )}

              <Select fullWidth name="addressType" value={addr.addressType || ""} onChange={(e) => handleAddressChange(idx, e)} displayEmpty margin="dense">
                <MenuItem value="">Select Address Type</MenuItem>
                <MenuItem value="Communication">Communication</MenuItem>
                <MenuItem value="Billing">Billing</MenuItem>
                <MenuItem value="Office">Office</MenuItem>
              </Select>

              <TextField fullWidth label="Address Line 1" name="address_1" value={addr.address_1 || ""} onChange={(e) => handleAddressChange(idx, e)} margin="dense" />
              <TextField fullWidth label="Address Line 2" name="address_2" value={addr.address_2 || ""} onChange={(e) => handleAddressChange(idx, e)} margin="dense" />
              <TextField fullWidth label="Address Line 3" name="address_3" value={addr.address_3 || ""} onChange={(e) => handleAddressChange(idx, e)} margin="dense" />
              <TextField fullWidth label="Street No" name="streetNo" value={addr.streetNo || ""} onChange={(e) => handleAddressChange(idx, e)} margin="dense" />
              <TextField fullWidth label="LandMark" name="landMark" value={addr.landMark || ""} onChange={(e) => handleAddressChange(idx, e)} margin="dense" />
              <TextField fullWidth label="Loacality" name="locality" value={addr.locality || ""} onChange={(e) => handleAddressChange(idx, e)} margin="dense" />
              <TextField fullWidth label="City" name="city" value={addr.city || ""} onChange={(e) => handleAddressChange(idx, e)} margin="dense" />
              <TextField fullWidth label="State" name="state" value={addr.state || ""} onChange={(e) => handleAddressChange(idx, e)} margin="dense" />
              <TextField fullWidth label="Pincode" name="pincode" value={addr.pincode || ""} onChange={(e) => handleAddressChange(idx, e)} margin="dense" />
              <Button onClick={() => removeAddress(idx)} color="error" sx={{ mt: 1 }}>Remove Address</Button>
            </Box>
          ))}
          <Button onClick={addAddress} sx={{ mt: 1 }}>Add Address</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">{editing ? "Update" : "Save"}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}